/**
 * CertificateStep Component
 * Step 3: Certificate of Approval (C.A.) fields for EPI
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { EpiFormData, EpiEfficiency } from '../../../../../types/epi';
import { CertificateStepProps } from '../EPIFormWizard.types';

export const CertificateStep: React.FC<CertificateStepProps> = ({
  disabled = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<EpiFormData>();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Informe os dados do Certificado de Aprovação (C.A.) do EPI.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        O C.A. é obrigatório para comercialização de EPIs no Brasil (NR-6).
      </Alert>

      <Grid container spacing={3}>
        {/* Número do C.A. */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="metodo"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Número do C.A."
                fullWidth
                required
                error={Boolean(errors.metodo)}
                helperText={errors.metodo?.message || 'Ex: 12345'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Marca/Modelo */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="brandModel"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Marca/Modelo"
                fullWidth
                error={Boolean(errors.brandModel)}
                helperText={errors.brandModel?.message || 'Ex: 3M Peltor X5'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Início da Validade */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="inicioValidade"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value || ''}
                label="Início da Validade"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.inicioValidade)}
                helperText={errors.inicioValidade?.message || 'Data de emissão do C.A.'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Fim da Validade */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="fimValidade"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Validade do C.A."
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.fimValidade)}
                helperText={errors.fimValidade?.message || 'Data de vencimento do C.A.'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Atenuação */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="atenuacao"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Atenuação / NRR"
                fullWidth
                error={Boolean(errors.atenuacao)}
                helperText={errors.atenuacao?.message || 'Ex: NRR 25 dB (para protetores auriculares)'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* EPI Eficaz */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="efficiency"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={Boolean(errors.efficiency)} disabled={disabled}>
                <InputLabel>EPI Eficaz</InputLabel>
                <Select
                  {...field}
                  value={field.value ?? ''}
                  label="EPI Eficaz"
                  onChange={(e) => field.onChange(e.target.value || undefined)}
                >
                  <MenuItem value="">Não informado</MenuItem>
                  <MenuItem value={EpiEfficiency.EFICAZ}>Eficaz</MenuItem>
                  <MenuItem value={EpiEfficiency.NAO_EFICAZ}>Não Eficaz</MenuItem>
                </Select>
                {errors.efficiency && (
                  <FormHelperText>{errors.efficiency.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CertificateStep;
